import {nextRoutes} from '@local/config/sanity.mjs'
import {defaultLocaleId} from '@local/config/locales.mjs'
import {LinkInput, LinkOutput} from '@local/types'
// import {URLPattern} from 'next/server'
import {URLPattern} from 'urlpattern-polyfill' // TODO replace with a regex?

const getPatterns = (rules, from): [URLPattern, (any) => void] => (
	rules.map?.(rewrite => {
		if (!rewrite[from]) return null
		return (rewrite[from].includes('/:') || rewrite[from].includes('*')) && [
			new URLPattern({pathname: rewrite[from]}),
			({pathname}) => ({pathname: rewrite[from], params: pathname.groups}),
		]
	}).filter(Boolean)
)

const getPattern = (pathname, patterns): {pathname?: string, params?: any} => {
	let result = {}

	for (const [pattern, handler] of patterns) {
		const patternResult = pattern.exec({pathname})
		if (patternResult !== null && 'pathname' in patternResult) {
			result = handler(patternResult)
			break
		}
	}

	return result
}

export const rewriteFromRules = (rules, pathname, fromDestination = false) => {
	if (!rules || !rules.length) return null

	const pattern = getPattern(pathname, fromDestination ? getPatterns(rules, 'destination') : getPatterns(rules, 'source'))
	const rewriteRule = rules.filter(rewrite => {
		const r = rewrite[fromDestination ? 'destination' : 'source']
		return r === pattern.pathname ||
		(defaultLocaleId && r === '/' + defaultLocaleId + pattern.pathname) ||
		r === pathname
	})

	const result = rewriteRule[0]?.[fromDestination ? 'source' : 'destination']

	let i = 0

	const rewrite = pattern.params
		? result?.replaceAll(
			new RegExp(`/:(${Object.keys(pattern.params).join('|')})`, 'gi'),
			(match, group) => match.replace(':' + group, pattern.params[group]),
		).replaceAll(
			/\*/gi,
			() => {
				return pattern.params?.[i++]
			})
		: result

	return rewrite
}

const hrefWithLocale = (href = '', locale = defaultLocaleId) => {
	return `/${locale}${href === '/' ? '' : href}`
}

const rewrite = (rewriteRules, href) => {
	const result = rewriteFromRules(rewriteRules, href, true) || href
	return result?.startsWith(`/${defaultLocaleId}`) ? result.replace(new RegExp(`/${defaultLocaleId}/?`), '/') : result
}

export const rewriteDocumentsSlugs = (rewriteRules, document) => {
	return document.slugs && Object.keys(document.slugs)
		.map(key => {
			const slug = document._type === 'home' ? '/' : document.slugs[key]
			return {locale: key, ...resolveLink({locale: key, _type: document._type, slug, parentSlug: document.parentSlugs[key]}, key, rewriteRules)}
		})
		.filter(locale => locale.href)
}

export const resolveLink: (link: LinkInput | string, locale, rewriteRules) => LinkOutput | undefined = (link, locale, rewriteRules) => {
	if (!link) return undefined

	if (typeof link === 'string') {
		if (link.indexOf('http') === -1) {
			const href = rewrite(rewriteRules, hrefWithLocale(link, locale))
			return {href}
		} else {
			return {href: link}
		}
	} else {
		if ((link.selectedTab?.startsWith('external') || link._type?.startsWith('external')) || link.url) {
			return {href: link.url, label: link.label}
		} else if (link.selectedTab?.startsWith('internal') || link.slug !== undefined || link._type === 'home') {
			const {
				_type,
				label,
				slug,
				query,
			} = link

			const parentSlug = link.parentSlug || link.parentPage?.slug
			const parentSlugStr = (typeof parentSlug === 'string' ? parentSlug : parentSlug?.current) || ''
			const slugStr = (typeof slug === 'string' ? slug : slug?.current) || ''

			let href = nextRoutes[_type]
			if (!href) return {href, label}
			href = href.replace(':parent', `${parentSlugStr}`)
			href = href.replace(':slug', `${slugStr}`)
			href = rewrite(rewriteRules, hrefWithLocale(href, locale))

			if (query) {
				href += `?${query}`
			}

			href = href.replaceAll('//', '/')

			return {href, label}
		}
	}
}
